import "./config/dayjs";
import React, { useEffect, useMemo, useState } from "react";
import ReactDOM from "react-dom/client";
import { useTranslation } from "react-i18next";
import { BrowserRouter as Router, Route, Switch } from "react-router-dom";
import { ApolloClient, ApolloProvider, InMemoryCache, HttpLink } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { onError } from "@apollo/client/link/error";
import * as serviceWorker from "./serviceWorker";
import { possibleTypes } from "./possibleTypes.json";
import { ContextApp, useContextApp } from "@config/Context/contextApp";
import { BookingContextProvider } from "@modules/booking/context/BookingContext";
import { ContextEDHProvider } from "./context/ContextEDH";
import { Helmet } from "react-helmet";
import { Loading, Text, ThemeStore, Toaster } from "@foris/avocado-suite";
import { loadPublicConfig } from "@services/collections/identity/publicConfigServices";
import { ENV, IDENTITY_URL } from "@common/config";
import { buildAuthHeaders } from "@config/apollo/authHeaders";
import { getIdentityToken } from "@context/useIdentityTokenStore";
import {
  getUrlWithoutTrailingSlash,
  setDarwinApiUrl,
  setDarwinLegacyUrl,
  usePublicConfigStore,
} from "@context/usePublicConfigStore";
import GetAuth from "./modules/login/GetApp";
import Shell from "./config/routes/Shell";
import TenantNotFound from "@modules/errors/tenant-not-found";
import "@config/i18n";
import { removeRangeTime } from "@config/CalendarRanges/calendarRanges";

const hasMultitenant =
  (import.meta.env.VITE_HAS_MULTITENANT ?? import.meta.env.REACT_APP_HAS_MULTITENANT) === "true";
const legacyDarwinApiUrl =
  import.meta.env.VITE_DARWINED_URL ?? import.meta.env.REACT_APP_DARWINED_URL;

const handleThemeChange = (event: MediaQueryListEvent | MediaQueryList) => {
  const mode = event.matches ? "dark" : "light";
  ThemeStore.setThemeMode(mode);
};

const AppDOM = () => {
  const [isLoadingPublicConfig, setIsLoadingPublicConfig] = useState(true);
  const [hasErrors, setHasErrors] = useState(false);
  const contextApp = useContextApp();
  const darwinLegacyUrl = usePublicConfigStore(state => state.darwinLegacyUrl);
  const darwinGraphqlApiUrl = usePublicConfigStore(state => state.darwinGraphqlApiUrl);

  const { t } = useTranslation();

  const apolloClient = useMemo(() => {
    if (!darwinGraphqlApiUrl || isLoadingPublicConfig || hasErrors) {
      return null;
    }

    const logoutLink = onError(({ networkError }) => {
      if (networkError && networkError["statusCode"] === 401) {
        window.location.href = `${getUrlWithoutTrailingSlash(darwinLegacyUrl)}/usuarios/logout`;
      }
    });

    // Read on every request: the Identity token is renewed while the app is open.
    const authLink = setContext((_, { headers }) => ({
      headers: buildAuthHeaders(headers, {
        token: localStorage.getItem("token"),
        identityToken: getIdentityToken(),
      }),
    }));

    const cache = new InMemoryCache({ possibleTypes });

    const httpLink = new HttpLink({
      uri: darwinGraphqlApiUrl,
    });
    const middleware = authLink.concat(logoutLink);
    const link = middleware.concat(httpLink);

    return new ApolloClient({
      cache,
      link,
      defaultOptions: {
        watchQuery: {
          fetchPolicy: "no-cache",
          errorPolicy: "ignore",
        },
        query: {
          fetchPolicy: "no-cache",
          errorPolicy: "all",
        },
      },
    });
  }, [darwinGraphqlApiUrl, isLoadingPublicConfig, hasErrors]);

  useEffect(() => {
    if ((import.meta.env.VITE_LOCK_EDIT ?? import.meta.env.REACT_APP_LOCK_EDIT) === "false") {
      contextApp.setLockEdit(false);
    } else {
      contextApp.setLockEdit(true);
    }
  }, [contextApp]);

  useEffect(() => {
    const hasLocalStorageTheme = window.localStorage.getItem("avocado-theme");

    if (hasMultitenant) {
      loadPublicConfig({
        identityUrl: IDENTITY_URL,
        environment: ENV,
        onSuccess: response => {
          const legacyUrl = getUrlWithoutTrailingSlash(
            response?.darwin_legacy_url || response?.darwin_api_url || "",
          );
          if (legacyUrl && window.location.pathname.startsWith("/usuarios/login_foris")) {
            window.location.replace(
              `${legacyUrl}${window.location.pathname}${window.location.search}`,
            );
            return;
          }
          setDarwinApiUrl(response?.darwin_api_url);
          setDarwinLegacyUrl(response?.darwin_legacy_url);
          setIsLoadingPublicConfig(false);
        },
        onError: error => {
          setIsLoadingPublicConfig(false);
          setHasErrors(true);
          console.error("Error loading public config:", error);
        },
      });
    } else {
      setDarwinApiUrl(legacyDarwinApiUrl);
      setIsLoadingPublicConfig(false);
    }

    // Set default theme
    if (window.matchMedia && !hasLocalStorageTheme) {
      const themeQuery = window.matchMedia("(prefers-color-scheme: dark)");

      handleThemeChange(themeQuery);
      themeQuery.addEventListener("change", handleThemeChange);
    } else {
      ThemeStore.toggleBodyTheme();
    }

    // Cleanup function
    return () => {
      setDarwinApiUrl(null);
      removeRangeTime();
    };
  }, []);

  return (
    <>
      <Helmet>
        {!!contextApp?.user?.id && (
          <script
            src="https://plausible.io/js/script.pageview-props.tagged-events.js"
            data-domain={window?.location?.hostname}
            defer
          ></script>
        )}
      </Helmet>

      {isLoadingPublicConfig && <Loading />}

      {hasErrors && (
        <TenantNotFound title={t("tenant.not-found.title")}>
          <Text type="lg">{t("tenant.not-found.description")}</Text>
          <Text type="lg">{t("tenant.not-found.contact")}</Text>
        </TenantNotFound>
      )}

      {!isLoadingPublicConfig && !hasErrors && !!darwinGraphqlApiUrl && (
        <ApolloProvider client={apolloClient}>
          <ContextApp.Provider value={contextApp}>
            <ContextEDHProvider>
              <BookingContextProvider>
                <Router>
                  <Switch>
                    <Route path="/auth/:token?" component={GetAuth} />
                    <Route path="/" component={Shell} />
                  </Switch>
                </Router>

                <Toaster />
              </BookingContextProvider>
            </ContextEDHProvider>
          </ContextApp.Provider>
        </ApolloProvider>
      )}
    </>
  );
};

const root = ReactDOM.createRoot(document.getElementById("root"));
root.render(<AppDOM />);

serviceWorker.unregister();
